import { css } from "@mds/core";

export const navigationCSS = css`
	@supports (--foo: "mds") {
		/* 	###################################################################### */
		/**	Component tokens
			   Use the design tokens to set these component tokens.
		   */
		:host {
			--display: block;
			--direction: row;
			--background-color: var(--mds-color-background-pale);
			--justify: start;
			--align: center;
			--padding: 0;
			--gap: var(--mds-spacing-4);
			--columns: auto 1fr 1fr auto;
			--column: 1;
			--rows: 1fr;
			--font-weight: var(--mds-typography-font-gui-weight-light);
			--line-weight-horzontal: 2px;
		}

		/* 	###################################################################### */
		/**	Navigation container rules
			  This is the main rule set.
		   */
		:host {
			padding: var(--pad-responsive, var(--gap)) 0;
			padding-inline: var(--pad-responsive) var(--pad-responsive);
			display: var(--display);
			grid-template-columns: var(--columns);
			grid-template-rows: var(--rows);
			justify-content: var(--justify);
			align-items: var(--align);
			gap: var(--gap);
			flex-wrap: wrap;
			text-align: var(--justify);
			z-index: 1;
			background-color: var(--background-color);
		}

		.content,
		.corporate,
		.commerce {
			display: var(--display);
			flex-direction: var(--direction);
			justify-content: var(--justify);
			gap: var(--gap);
			white-space: nowrap;
		}

		.content {
			margin-inline-start: var(--mds-spacing-5);
		}

		.corporate {
			justify-content: flex-end;
		}

		.commerce {
			margin-inline-start: var(--mds-spacing-5);
			justify-self: var(--justify);
		}
		a:link,
		a:active,
		a:visited,
		a:focus-visible,
		a:hover {
			text-decoration: none;
			border-bottom: 0;
			color: transparent;
		}
		.mds-logo {
			align-self: center;
			width: 140px;
			height: 48px;
			background: transparent var(--mds-datauri-logos-mediq) no-repeat 0
				50%;
			color: transparent;
		}

		.mds-logo svg {
			vertical-align: bottom;
		}

		:host ::slotted(*) {
			display: contents;
		}

		/* 	###################################################################### */
		/**	Rules for mobile menu  */
		:host([break]) {
			padding: var(--mds-spacing-2) 0;
			padding-inline-end: var(--padding-responsive, var(--gap));
			align-items: center;
			width: 100%;
			overflow: hidden;
		}

		:host([break]) .mds-logo {
			width: 100%;
			grid-row: 1;
			grid-column: 2;
		}

		:host([break]) .content,
		:host([break]) .corporate {
			position: absolute;
			top: var(--offset-top);
			display: flex;
			flex-direction: column;
			gap: var(--gap);
			left: -100vw;
			margin-inline-start: 0;
			padding-inline-start: 0;
			padding: var(--mds-spacing-2) 0 var(--mds-spacing-4);
			width: 100%;
			transition: left 0.2s ease-in;
			background-color: var(--mds-color-background-light);
			overflow: hidden;
			z-index: 3;
		}

		:host([break][isopen]) .content,
		:host([break][isopen]) .corporate {
			left: 0;
			transition: left 0.2s ease-out;
		}

		:host([break]) .corporate {
			grid-column: 3;
			grid-row: 3;
			background-color: transparent;
			padding-bottom: var(--mds-spacing-6);
		}

		:host([break]) .corporate::before {
			position: absolute;
			top: 0;
			content: "";
			width: 100vw;
			height: calc(100% - 8px);
			z-index: -1;
			background-color: var(--mds-color-background-light);
		}

		:host([break]) .corporate::after {
			position: absolute;
			bottom: 0px;
			content: "";
			width: 100vw;
			height: 8px;
			z-index: 1;
			background: linear-gradient(
				180deg,
				rgba(0, 0, 0, 0.24) 0%,
				rgba(0, 0, 0, 0) 100%
			);
		}

		:host([break]) .commerce {
			align-content: end;
			justify-items: end;
			text-align: end;
			grid-column: 3;
			grid-row: 1;
			z-index: 1;
		}

		/* 	###################################################################### */
		/**	Properties for navigation */
		:host {
			--display: grid;
			--gap: var(--mds-spacing-5);
		}

		:host .content {
			--display: flex;
			--justify: start;
			--font-weight: var(--mds-typography-font-gui-weight-bold);
		}

		:host .corporate {
			--display: flex;
			--justify: flex-end;
		}

		:host .commerce {
			--display: flex;
			--justify: end;
			--line-weight-horzontal: 0;
			--gap: var(--mds-spacing-6);
		}

		/* 	###################################################################### */
		/**	Properties for mobile menu  */
		:host([break]) {
			--display: grid;
			--columns: var(--pad-responsive, var(--gap)) 120px 1fr 3rem
				var(--pad-responsive, var(--gap));
			--rows: auto auto auto;
			--justify: start;
			--gap: 0;
		}

		:host([break]) .content {
			--active-color-bottom: var(--mds-color-background-pale);
		}

		:host([break]) .corporate {
			--active-color-bottom: transparent;
		}

		:host([break]) .commerce {
			--display: flex;
			--direction: row;
			--justify: end;
			--gap: 0;
		}

		/* 	###################################################################### */
		/**	Properties for hamburger menu  */
		:host([break]) .hamburger {
			position: relative;
			right: calc(var(--mds-spacing-1) * -1);
			grid-column: 4;
			grid-row: 1;
			border-radius: var(--mds-line-border-base-radius);
		}

		:host([break]) .hamburger span {
			position: absolute;
			top: -100vh;
			z-index: -100;
		}

		:host([break]) .hamburger::before {
			font-family: var(--mds-icon-font-base-family);
			content: var(--mds-icon-glyphs-base-menu-button);
			padding: 0
				clamp(
					var(--mds-spacing-1),
					var(--pad-responsive, var(--gap)),
					var(--mds-spacing-4)
				);
			line-height: 2.8;
		}

		:host([break][isopen]) .hamburger::before {
			content: var(--mds-icon-glyphs-base-close);
		}

		:host([break]) .hamburger::after {
			font-family: var(--mds-icon-font-base-family);
			content: "";
			position: absolute;
			bottom: calc(var(--mds-typography-scale-height-s) * -1rem);
			left: clamp(
				var(--mds-spacing-1),
				var(--pad-responsive, var(--gap)),
				var(--mds-spacing-4)
			);
			width: 1rem;
			height: 1rem;
			background: white;
			transform: rotate(45deg);
			opacity: 0;
			transition: opacity 0.2s step-start;
		}

		:host([break][isopen]) .hamburger::after {
			opacity: 1;
			transition: opacity 0.2s step-end;
		}
	}
`;
